import React, { useEffect, useMemo, useState } from "react";
import { LuImage } from "react-icons/lu";
import { useForm } from "react-hook-form";
import NumberFormat from "react-number-format";
import ImagePicker from "./ImagePicker";
import { v4 } from "uuid";
import Loading from "../../../shared/components/Loading/Loading";
import { storage } from "../../../config/firebase";
import requestApi from "../../../apirest";
import { useHistory, useParams } from "react-router-dom";
import VariationForm from "./VariationForm";
import ProductSizeTable from "./ProductSizeTable";
import Switch from "react-switch";
import { ages, categories, tableSizeFactories, lines } from "../../../data/product";
import "./Create.css";

export default function CreateProduct() {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState,
    getValues,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      code: "JS",
      age: "adult",
    },
  });

  const age = watch("age");
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [hasInfluencer, setHasInfluencer] = useState(0);
  const [tableSize, setTableSize] = useState(tableSizeFactories.adult);
  const [variations, setVariations] = useState([variationFactory(true, age)]);
  const [isFetchingProduct, setIsFetchingProduct] = useState(false);
  const [isRemovingProduct, setIsRemovingProduct] = useState(false);
  const params = useParams();
  const history = useHistory();
  const showDiscountFields = watch("sale");
  const productName = watch("name", "");
  const salePrice = useMemo(() => calcDiscount(price, discount), [price, discount]);
  const mainImage = variations[0].pictures[0];
  const isLoading = isFetchingProduct || formState.isSubmitting || isRemovingProduct;
  const isEditing = !!params.id;

  useEffect(() => {
    if (isEditing) {
      setIsFetchingProduct(true);
      requestApi("/products/findByIdWithDeletedVariants", params.id, "get").then((res) => {
        const product = res.data.doc;
        const { variants, tableSize, ...rest } = product;
        for (const key in rest) {
          const value = rest[key];
          switch (key) {
            case "price":
              setPrice(value);
              break;
            case "discount":
              setDiscount(value);
              break;
            case "hasInfluencer":
              setHasInfluencer(value);
              break;
            default:
              setValue(key, value);
              break;
          }
        }
        setVariations(setVariationsLocalData(variants));
        setTableSize(tableSize);
        setIsFetchingProduct(false);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formState.touched.urlName) return;
    const slug = productName
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .split(" ")
      .join("-")
      .toLowerCase();
    setValue("urlName", slug);
  }, [productName, formState]);

  function addVariation() {
    setVariations([...variations, variationFactory(false, age)]);
  }

  function removeVariation(variation, checked) {
    const index = variations.findIndex((v) => v.key === variation.key);
    variations[index].isDeleted = checked;
    setVariations([...variations]);
  }

  function setMainVariationImage(image) {
    variations[0].pictures[0] = image;
    setVariations([...variations]);
  }

  function onVariationChange(variation) {
    const index = variations.findIndex((v) => v.key === variation.key);
    variations[index] = variation;
    setVariations([...variations]);
  }

  function onHasInfluenceChange(valueInfluencer) {
    setHasInfluencer(valueInfluencer);
  }

  async function createOrUpdateProduct(data) {
    const variants = await processVariations(variations);
    const product = {
      ...data,
      price,
      variants,
      discount,
      salePrice,
      tableSize,
      hasInfluencer,
    };
    const method = isEditing ? "patch" : "post";
    await requestApi(`/products`, { product }, method);
    history.replace("/admin-panel/Product");
  }

  async function removeProduct() {
    setIsRemovingProduct(true);
    const product = getValues();
    await requestApi(`/products`, { idProduct: product._id }, "delete");
    history.replace("/admin-panel/Product");
  }

  const shortDetailsValue = watch("shortDetails", "");
  const descriptionValue = watch("description", "");

  return (
    <div className="cp-page">
      {isLoading && <Loading overlay />}

      {/* ── Top bar ── */}
      <div className="cp-topbar">
        <div>
          <p className="cp-topbar-breadcrumb">Produtos</p>
          <h1 className="cp-topbar-title">
            {isEditing ? "Editar produto" : "Novo produto"}
          </h1>
        </div>
        <div className="cp-topbar-actions">
          <button
            type="button"
            className="cp-btn cp-btn--secondary"
            onClick={() => history.replace("/admin-panel/Product")}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="product-details"
            className="cp-btn cp-btn--primary"
          >
            Salvar produto
          </button>
        </div>
      </div>

      <form id="product-details" onSubmit={handleSubmit(createOrUpdateProduct)}>
        <input type="hidden" name="_id" ref={register()} />

        {/* ── Corpo: mídia + informações (mesma altura) ── */}
        <div className="cp-body">
          <div className="cp-media-col">
            <div className="cp-card cp-card--fill">
              <p className="cp-card-title">
                Mídia do produto
                <span className="cp-card-title-hint" title="Imagem principal da variante em destaque">
                  <LuImage size={14} />
                </span>
              </p>
              <div style={{ height: 440 }}>
                <ImagePicker
                  onChange={(image) => setMainVariationImage(image)}
                  defaultImage={mainImage}
                />
              </div>
            </div>
          </div>

          {/* Informações do produto */}
          <div className="cp-card">
            <p className="cp-card-title">Informações do produto</p>

            <div className="cp-field">
              <label className="cp-label" htmlFor="name">Nome do produto</label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Ex.: Camiseta Oversized Algodão"
                className="cp-input"
                ref={register({ required: false })}
              />
            </div>

            <div className="cp-field-row">
              <div className="cp-field">
                <label className="cp-label" htmlFor="code">Código</label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  className="cp-input"
                  ref={register()}
                />
              </div>
              <div className="cp-field">
                <label className="cp-label" htmlFor="urlName">URL</label>
                <input
                  id="urlName"
                  name="urlName"
                  type="text"
                  className="cp-input"
                  ref={register()}
                />
              </div>
            </div>

            <div className="cp-field-row">
              <div className="cp-field">
                <label className="cp-label" htmlFor="category">Categoria</label>
                <select
                  id="category"
                  name="category"
                  className="cp-select"
                  ref={register()}
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cp-field">
                <label className="cp-label" htmlFor="line">Coleção (linha)</label>
                <select id="line" name="line" className="cp-select" ref={register()}>
                  {lines.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="cp-field">
              <label className="cp-label" htmlFor="age">Idade</label>
              <select
                id="age"
                name="age"
                className={`cp-select${isEditing ? " cp-select--disabled" : ""}`}
                ref={register()}
              >
                {ages.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cp-field">
              <label className="cp-label" htmlFor="shortDetails">Descrição</label>
              <textarea
                id="shortDetails"
                name="shortDetails"
                placeholder="Detalhes sobre o produto, tecido, modelagem, cuidados..."
                rows="4"
                className="cp-textarea"
                ref={register()}
              />
              <div className="cp-field-footer">
                <span className="cp-char-count">
                  {shortDetailsValue.length} caracteres
                </span>
              </div>
            </div>

            <div className="cp-field">
              <label className="cp-label" htmlFor="description">Composição</label>
              <textarea
                id="description"
                name="description"
                rows="4"
                className="cp-textarea"
                ref={register()}
              />
              <div className="cp-field-footer">
                <span className="cp-char-count">
                  {descriptionValue.length} caracteres
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tamanhos (logo após mídia/informações) ── */}
        <div className="cp-full-section">
          <div className="cp-card">
            <p className="cp-card-title">Tamanhos</p>
            <ProductSizeTable sizes={tableSize} onChange={setTableSize} />
          </div>
        </div>

        {/* ── Preço + Atributos lado a lado ── */}
        <div className="cp-full-section cp-two-col-row">
          <div className="cp-card">
            <p className="cp-card-title">Preço</p>

            <div className="cp-field">
              <label className="cp-label" htmlFor="price">Preço de venda</label>
              <NumberFormat
                id="price"
                name="price"
                decimalSeparator=","
                prefix="R$"
                thousandSeparator="."
                inputType="number"
                decimalScale="2"
                fixedDecimalScale={true}
                className="cp-number-input"
                value={price}
                onValueChange={(e) => setPrice(e.floatValue)}
              />
            </div>

            <div className="cp-checkboxes">
              <label className="cp-checkbox-row">
                <input type="checkbox" name="sale" ref={register()} />
                <span className="cp-checkbox-label">Produto em promoção</span>
              </label>
            </div>

            {showDiscountFields && (
              <div className="cp-discount-grid">
                <div className="cp-field">
                  <label className="cp-label" htmlFor="discount">Desconto</label>
                  <NumberFormat
                    id="discount"
                    name="discount"
                    suffix="%"
                    inputType="text"
                    className="cp-number-input"
                    value={discount}
                    onValueChange={(e) => setDiscount(e.floatValue)}
                  />
                </div>
                <div className="cp-field">
                  <label className="cp-label" htmlFor="salePrice">
                    Preço com desconto
                  </label>
                  <NumberFormat
                    id="salePrice"
                    name="salePrice"
                    decimalSeparator=","
                    prefix="R$"
                    thousandSeparator="."
                    inputType="number"
                    decimalScale="2"
                    fixedDecimalScale={true}
                    className="cp-number-input"
                    readOnly
                    value={salePrice}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="cp-card">
            <p className="cp-card-title">Atributos</p>
            <div className="cp-checkboxes">
              <label className="cp-checkbox-row">
                <input type="checkbox" name="new" ref={register()} />
                <span className="cp-checkbox-label">Produto é lançamento</span>
              </label>
              <label className="cp-checkbox-row">
                <input type="checkbox" name="natural" ref={register()} />
                <span className="cp-checkbox-label">O produto é natural</span>
              </label>
            </div>
          </div>
        </div>

        {/* ── Variantes (por último) ── */}
        <div className="cp-full-section">
          <div className="cp-card">
            <p className="cp-card-title">Variantes</p>
            {variations.map((variation, i) => {
              const isLast = i === variations.length - 1;
              return (
                <React.Fragment key={variation.key || variation._id}>
                  <div className="cp-variation-card">
                    <VariationForm
                      variation={variation}
                      hasInfluencer={hasInfluencer}
                      age={age}
                      onChange={(v) => onVariationChange(v)}
                      onChangeHasInfluence={(has) => onHasInfluenceChange(has)}
                    />
                    <div className="cp-variation-footer">
                      <span className="cp-variation-toggle-label">
                        Desativar variante ({variation.colorName}):
                        <Switch
                          checked={!!variation.isDeleted}
                          onChange={(checked) => removeVariation(variation, checked)}
                          onColor="#86d3ff"
                          onHandleColor="#2693e6"
                          handleDiameter={18}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 3px rgba(0,0,0,0.6)"
                          activeBoxShadow="0px 0px 1px 7px rgba(0,0,0,0.2)"
                          height={12}
                          width={28}
                          className="react-switch"
                        />
                      </span>
                    </div>
                  </div>
                  {isLast && (
                    <button
                      type="button"
                      className="cp-add-variation-btn"
                      onClick={addVariation}
                    >
                      + Adicionar cor
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </form>

      {/* ── Footer actions ── */}
      <div className="cp-footer-actions">
        {params.id && (
          <button
            type="button"
            className="cp-btn cp-btn--danger"
            onClick={removeProduct}
          >
            Remover produto
          </button>
        )}
        <button
          type="submit"
          form="product-details"
          className="cp-btn cp-btn--primary"
        >
          Salvar produto
        </button>
      </div>
    </div>
  );
}

function calcDiscount(price, discount) {
  const discountPercent = discount / 100;
  const discountValue = price * discountPercent;
  return price - discountValue;
}

function variationFactory(principal = false, age = "adult") {
  return {
    key: v4(),
    color: { hex: "#0000ff" },
    colorName: "",
    colorImage: null,
    principal,
    pictures: [],
    videos: [],
    influencers: [],
  };
}

async function processVariations(variations = []) {
  return await Promise.all(
    variations.map(async (variation) => {
      const { pictures, colorImage, video, thumbnail, videos, influencers } = variation;

      const picturesList = await Promise.all(
        pictures.map(async (picture) => {
          const { file, filename } = picture;
          if (!file) return picture;
          const url = await uploadImage("images", file, filename);
          return { filename, url };
        })
      );

      const colorImageData = { ...colorImage };
      if (colorImageData && colorImageData.file) {
        const { file, filename } = colorImageData;
        const url = await uploadImage("images", file, filename);
        delete colorImageData.file;
        colorImageData.url = url;
      }

      const videosList = [...videos];
      if (video && video.file) {
        const { file, filename } = video;
        const videoUrl = await uploadImage("videos", file, filename);
        videosList[0] = { ...videosList[0], filename, url: videoUrl };
      }

      if (thumbnail && thumbnail.file) {
        const { file, filename } = thumbnail;
        const thumbnailUrl = await uploadImage("videos", file, filename);
        videosList[0] = {
          ...videosList[0],
          imageName: filename,
          urlImage: thumbnailUrl,
        };
      }

      const influencersList = await Promise.all(
        influencers.map(async (influencer) => {
          const { file, filename } = influencer;
          if (!file) return influencer;
          const url = await uploadImage("images", file, filename);
          return { filename, url };
        })
      );

      const updatedVariation = {
        ...variation,
        colorImage: colorImageData,
        pictures: picturesList,
        videos: videosList,
        influencers: influencersList,
      };

      delete updatedVariation.key;
      delete updatedVariation.video;
      delete updatedVariation.thumbnail;

      return updatedVariation;
    })
  );
}

async function uploadImage(bucketName, file, fileName) {
  try {
    const bucket = storage.ref(bucketName);
    await bucket.child(fileName).put(file);
    return await bucket.child(fileName).getDownloadURL();
  } catch (error) {
    console.error("Não foi possível adicionar imagem", file, fileName);
    return "";
  }
}

function setVariationsLocalData(variations) {
  return variations.map((v) => {
    v.key = v4();
    return v;
  });
}
